// v3E.3 — Wearables stub page (Apple Health / Google Fit / Fitbit "connect" demo).
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Watch, Activity, RefreshCw, Power, ChevronRight, Sparkles } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";

const PROVIDER_TINT = {
    apple_health: "from-zinc-900 to-zinc-700",
    google_fit:   "from-emerald-500 to-teal-500",
    fitbit:       "from-blue-500 to-cyan-500",
};

const Wearables = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/wearables/connections");
            setItems(data.items || []);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onConnect = async (slug) => {
        try {
            setBusyId(slug);
            await api.post("/wearables/connect", { provider: slug });
            toast.success("Connection simulated (no real OAuth)");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not connect");
        } finally { setBusyId(null); }
    };

    const onSync = async (slug) => {
        try {
            setBusyId(slug);
            await api.post("/wearables/sync", { provider: slug });
            toast.success("Sync simulated — real device sync coming soon");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not sync");
        } finally { setBusyId(null); }
    };

    const onDisconnect = async (slug) => {
        try {
            setBusyId(slug);
            await api.delete(`/wearables/connect/${slug}`);
            toast.success("Disconnected");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not disconnect");
        } finally { setBusyId(null); }
    };

    return (
        <Layout>
            <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
                <AuthGuard>
                    <header className="mb-6" data-testid="wearables-page">
                        <p className="label-uppercase">Wearables</p>
                        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Connect your devices</h1>
                        <p className="mt-2 max-w-xl text-sm text-zinc-600 dark:text-zinc-400">
                            We're working on bringing in steps, heart-rate and sleep automatically from your phone & watch.
                            For now, you can preview the experience — manual health logging in <a className="font-medium text-brand-700 underline" href="/health">Health tracker</a> remains the source of truth.
                        </p>
                    </header>

                    <div className="mb-6 rounded-3xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200" data-testid="wearables-banner">
                        <Sparkles className="mr-1 inline-block h-4 w-4" />
                        <strong>Demo mode:</strong> Wearable sync is simulated — no real OAuth or vendor APIs are called yet. Real Apple Health / Google Fit launch in Phase v3F.
                    </div>

                    {loading ? (
                        <div className="grid gap-4 md:grid-cols-3">
                            {[1, 2, 3].map((i) => <div key={i} className="h-44 skeleton rounded-2xl" />)}
                        </div>
                    ) : (
                        <div className="grid gap-4 md:grid-cols-3" data-testid="wearables-grid">
                            {items.map((p) => {
                                const connected = !!p.connected;
                                const busy = busyId === p.slug;
                                return (
                                    <div key={p.slug} data-testid={`wearable-card-${p.slug}`} className="overflow-hidden rounded-3xl border border-zinc-100 bg-white shadow-soft dark:border-white/10 dark:bg-white/[0.03]">
                                        <div className={`h-28 bg-gradient-to-br ${PROVIDER_TINT[p.slug] || "from-zinc-500 to-zinc-700"} p-5`}>
                                            <div className="flex items-center gap-2 text-white">
                                                <Watch className="h-5 w-5" />
                                                <p className="text-sm font-bold">{p.name}</p>
                                            </div>
                                            <p className="mt-2 text-[11px] text-white/80">
                                                Supports: {(p.supports || []).join(", ")}
                                            </p>
                                        </div>
                                        <div className="p-5">
                                            <div className="flex items-center justify-between">
                                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${connected ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-zinc-100 text-zinc-700 dark:bg-white/10 dark:text-zinc-300"}`}>
                                                    {connected ? "Connected" : "Not connected"}
                                                </span>
                                                <span className="text-[11px] text-zinc-500 dark:text-zinc-400">
                                                    Last sync: {p.last_sync_at ? new Date(p.last_sync_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}
                                                </span>
                                            </div>
                                            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                                                {p.synced_measurements_count ? `${p.synced_measurements_count} measurements imported` : "No measurements yet"}
                                            </p>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {!connected ? (
                                                    <button onClick={() => onConnect(p.slug)} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50" data-testid={`wearable-connect-${p.slug}`}>
                                                        <Power className={`h-3 w-3 ${busy ? "animate-pulse" : ""}`} /> Connect
                                                    </button>
                                                ) : (
                                                    <>
                                                        <button onClick={() => onSync(p.slug)} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50" data-testid={`wearable-sync-${p.slug}`}>
                                                            <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Sync now
                                                        </button>
                                                        <button onClick={() => onDisconnect(p.slug)} disabled={busy} className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-3 py-1.5 text-xs text-zinc-500 hover:border-rose-200 hover:text-rose-600 disabled:opacity-50 dark:border-white/10 dark:text-zinc-400 dark:hover:border-rose-400/30" data-testid={`wearable-disconnect-${p.slug}`}>
                                                            Disconnect
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <a href="/health" className="mt-8 inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:border-brand-200 hover:text-brand-700 dark:border-white/10 dark:bg-transparent dark:text-zinc-200 dark:hover:border-brand-400/40 dark:hover:text-brand-300" data-testid="wearables-link-health">
                        <Activity className="h-4 w-4" /> Open Health tracker <ChevronRight className="h-3 w-3" />
                    </a>
                </AuthGuard>
            </section>
        </Layout>
    );
};

export default Wearables;
